import React from "react";
import { handleOpenFile } from '../../../../API/utils';
import { Switch, Tooltip, Select, Button, Upload } from "antd";
import { ClockCircleFilled, CheckCircleFilled, FolderOpenFilled, TagFilled, EyeFilled, DeleteFilled } from "@ant-design/icons";

const statusLabels = ["Definido", "Diseñado", "Implementado", "Validado", "Documentado"];

const statusMapping = {
  definido: "definited",
  diseñado: "designed",
  implementado: "implemented",
  validado: "validated",
  documentado: "documented"
};

const { Option } = Select;

export const isDisabled = (record) => {
  return !statusLabels.some((label, index) => {
    if (index === 0) return false;
    const key = statusMapping[label.toLowerCase()];
    return record[key];
  });
};

const Columns = (controls, setControls, setEditedControls, options, filters, handleOpenChangeLog, api) => [
  {
    title: "Activo",
    dataIndex: "asset",
    key: "asset",
    sorter: (a, b) => (a.assetName || "").localeCompare(b.assetName || ""),
    onHeaderCell: () => ({
      style: { backgroundColor: "#E0F8AD", color: "#006939", fontWeight: "bold" }
    }),
    showSorterTooltip: false,

    filteredValue: filters.dashboardAssetsFilter.length
      ? filters.dashboardAssetsFilter
      : undefined,

    filters: filters.assetFilterOptions?.map(asset => ({
      text: asset,
      value: asset
    })),

    onFilter: (value, record) => record.assetName === value,

    render: (_, record) => {
      const currentRecord = controls.find(control => control.code === record.code) || record;
      const isEvaluated = currentRecord.initialStatus === "Evaluado";
      return (
        <div style={isEvaluated ? { opacity: 0.5 } : {}}>
          {record.assetName}
        </div>
      );
    },

    width: '9%'
  },
  {
    title: "Dependencia",
    key: "dependency",
    sorter: (a, b) => (a.dependencyName || "").localeCompare(b.dependencyName || ""),
    onHeaderCell: () => ({
      style: { backgroundColor: "#E0F8AD", color: "#006939", fontWeight: "bold" }
    }),
    showSorterTooltip: false,
    render: (_, record) => {
      const currentRecord = controls.find(control => control.code === record.code) || record;
      const isEvaluated = currentRecord.initialStatus === "Evaluado";
      return (
        <div style={isEvaluated ? { opacity: 0.5 } : {}}>
          {record.dependencyName}
        </div>
      );
    },
    width: '10%'
  },
  {
    title: "Control",
    key: "control",
    sorter: (a, b) => (a.controlTitle || "").localeCompare(b.controlTitle || ""),
    onHeaderCell: () => ({
      style: { backgroundColor: "#E0F8AD", color: "#006939", fontWeight: "bold" }
    }),
    showSorterTooltip: false,
    render: (_, record) => {
      const currentRecord = controls.find(control => control.code === record.code) || record;
      const isEvaluated = currentRecord.initialStatus === "Evaluado";
      return (
        <div style={isEvaluated ? { opacity: 0.5 } : {}}>
          <Tooltip title={record.controlDescription || "Sin descripción"}>
            <span>{record.controlTitle}</span>
          </Tooltip>
        </div>
      );
    },
    width: '12%'
  },
  {
    title: "Responsable",
    dataIndex: "area",
    key: "areas",
    sorter: (a, b) => (a.area?.name || '').localeCompare(b.area?.name || ''),
    filteredValue: filters.dashboardAreasFilter.length
      ? filters.dashboardAreasFilter
      : undefined,
    filters: filters.areaFilterOptions?.map(area => ({
      text: area.name,
      value: area.name
    })),
    onFilter: (value, record) => record.area?.name === value,
    render: (_, record) => {
      const currentRecord = controls.find(control => control.code === record.code) || record;
      const isEvaluated = currentRecord.initialStatus === "Evaluado";
      const areaName = record.area?.name || "N/A";
      const truncatedName = areaName.length > 24 ? areaName.slice(0, 24) + "..." : areaName;

      return (
        <div style={isEvaluated ? { opacity: 0.5 } : {}}>
          {areaName.length > 24 ? (
            <Tooltip title={areaName}>
              {truncatedName}
            </Tooltip>
          ) : (
            areaName
          )}
        </div>
      );
    },
    onHeaderCell: () => ({
      style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' },
      className: 'custom-filter-icon'
    }),
    showSorterTooltip: false,
    width: '12%',
  },
  {
    title: "Estado",
    dataIndex: "status",
    key: "status",
    sorter: (a, b) => a.status.localeCompare(b.status),
    onHeaderCell: () => ({ style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' }, className: 'custom-filter-icon' }),
    showSorterTooltip: false,
    align: "center",
    width: "11%",
    filters: [
      {
        text: 'Evaluado',
        value: 'Evaluado',
      },
      {
        text: 'Pendiente',
        value: 'Pendiente',
      },
    ],
    filteredValue: filters.dashboardStatusFilter ? [filters.dashboardStatusFilter] : filters.isDashboardStageFilter ? ['Evaluado'] : undefined,
    onFilter: (value, record) => {
      // Si es un filtro combinado desde dashboard (etapa + Evaluado)
      if (filters.isDashboardStageFilter) {
        return record.initialStatus === 'Evaluado';
      }
      // Filtro normal de status
      return value === 'Evaluado' 
        ? record.initialStatus === 'Evaluado'
        : record.initialStatus !== 'Evaluado';
    },
    render: (status, record) => {
      const currentRecord = controls.find(control => control.code === record.code) || record;
      const statusOption = options.statusOptions.find(opt => opt.id === currentRecord.status_id);
      const isEvaluated = currentRecord.initialStatus === "Evaluado";
      const isAtLeastDesigned = currentRecord.designed;

      const handleChange = (newStatusId) => {
        if (isEvaluated) return; // No permitir cambios si ya está evaluado
        
        const selectedStatus = options.statusOptions.find(opt => opt.id === newStatusId);
        
        const updatedControls = controls.map(control => 
          control.code === record.code 
            ? { 
                ...control, 
                status: selectedStatus.name,
                status_id: selectedStatus.id,
              } 
            : control
        );
        
        setControls(updatedControls);
        setEditedControls(prev => new Set(prev).add(record.code));
      };
  
      return (
        <div style={isEvaluated ? { opacity: 0.5 } : {}}>
          <Select
            value={currentRecord.status_id}
            onChange={handleChange}
            bordered={false}
            dropdownMatchSelectWidth={false}
            style={{ width: 120 }}
            optionLabelProp="label"
            //disabled={!isSuperuser && (!hasAnyRole || !canEvaluationUpdate)}
            //disabled={isEvaluated || !isAtLeastDesigned}
          >
            {options.statusOptions?.map(option => (
              <Option 
                key={option.id} 
                value={option.id}
                label={
                  <Tooltip title={option.name}>
                    {option.name === "Evaluado" ? (
                      <CheckCircleFilled style={{ color: "#28A745", fontSize: 18 }} />
                    ) : (
                      <ClockCircleFilled style={{ color: "#FFA500", fontSize: 18 }} />
                    )}
                  </Tooltip>
                }
              >
                {option.name}
              </Option>
            ))}
          </Select>
        </div>
      );
    }
  },
  ...statusLabels.map((label, index) => {
  const lowerLabel = label.toLowerCase();
  const statusKey = statusMapping[lowerLabel];

  const shouldApplyFilter = filters.dashboardStageFilter?.toLowerCase() === lowerLabel;

    return {
      title: label,
      key: lowerLabel,
      align: "center",
      width: ["Implementado", "Documentado"].includes(label) ? '12%' : '8%',
      onHeaderCell: () => ({
        style: { backgroundColor: "#E0F8AD", color: "#006939", fontWeight: "bold" }
      }),
      filters: [{ text: 'Sí', value: true }, { text: 'No', value: false }],
      filteredValue: shouldApplyFilter ? [true] : undefined,
      onFilter: (value, record) => {
        const currentRecord = controls.find(control => control.code === record.code) || record;
        // Si es un filtro desde dashboard, solo verifica la etapa
        if (shouldApplyFilter) {
          return currentRecord[statusKey] === true;
        }
        // Filtro manual normal
        return currentRecord[statusKey] === value;
      },
      render: (_, record) => {
        const isDefinited = statusKey === "definited";
        const currentRecord = controls.find(control => control.code === record.code) || record;
        const isEvaluated = currentRecord.initialStatus === "Evaluado";
        const isCompleted = isEvaluated;

        // CASO DOCUMENTADO
        if (statusKey === "documented") {
          return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              {/* Switch */}
              <div style={{ opacity: isEvaluated ? 0.5 : 1, display: "flex", alignItems: "center" }}>
                <Switch
                  className="custom-assignment-switch"
                  checked={currentRecord[statusKey]}
                  disabled={isCompleted}
                  onChange={(checked) => {
                    setControls(prevControls =>
                      prevControls.map(control => {
                        if (control.code === record.code) {
                          const newControl = { 
                            ...control, 
                            [statusKey]: checked,
                            uploadedFile: checked ? control.uploadedFile : null, 
                            documentUrl: checked ? control.documentUrl : null 
                          };

                          if (checked) {
                            statusLabels.slice(0, index + 1).forEach(sLabel => {
                              newControl[statusMapping[sLabel.toLowerCase()]] = true;
                            });
                          } else {
                            statusLabels.slice(index).forEach(sLabel => {
                              newControl[statusMapping[sLabel.toLowerCase()]] = false;
                            });
                          }

                          return newControl;
                        }
                        return control;
                      })
                    );

                    setEditedControls(prev => new Set(prev).add(record.code));
                  }}
                />
              </div>

              {/* Íconos */}
              {currentRecord[statusKey] && (
                <>
                  <Upload
                    showUploadList={false}
                    beforeUpload={(file) => {
                      setControls(prev =>
                        prev.map(c =>
                          c.code === currentRecord.code
                            ? { ...c, uploadedFile: file, documentUrl: file.name }
                            : c
                        )
                      );
                      setEditedControls(prev => new Set(prev).add(record.code));
                      api.success({
                        message: "Documento agregado",
                        description: `Se cargó correctamente "${file.name}"`,
                      });
                      return false; 
                    }}
                    disabled={isEvaluated}
                  >
                    <Tooltip title={isEvaluated ? "" : "Subir Documento"}>
                      <Button disabled={isEvaluated} type="link" icon={<TagFilled />} />
                    </Tooltip>
                  </Upload>

                  {currentRecord.documentUrl && (
                    <Tooltip title="Ver Documento">
                      <Button
                        type="link"
                        icon={<EyeFilled />}
                        onClick={async () => {
                          try {
                            let blobUrl = null;

                            if (currentRecord.uploadedFile instanceof File) {
                              // Archivo local recién subido
                              blobUrl = URL.createObjectURL(currentRecord.uploadedFile);
                            } else if (
                              currentRecord.documentUrl &&
                              typeof currentRecord.documentUrl.url === "string"
                            ) {
                              // Archivo remoto: pasamos la URL al handleOpenFile
                              const fileData = await handleOpenFile(currentRecord.documentUrl.url, "file");
                              if (fileData instanceof Blob) {
                                blobUrl = URL.createObjectURL(fileData);
                              } else if (typeof fileData === "string") {
                                blobUrl = fileData;
                              }
                            } else {
                              console.warn("El documento no tiene una URL válida");
                            }

                            if (blobUrl) {
                              const newWindow = window.open(blobUrl, "_blank");
                              if (!newWindow)
                                console.warn("El navegador bloqueó la apertura de la nueva ventana");
                            }
                          } catch (error) {
                            console.error("Error al abrir el archivo:", error);
                          }
                        }}
                      />
                    </Tooltip>
                  )}

                </>
              )}
            </div>
          );
        }

        const switchElement = (
          <div style={isEvaluated ? { opacity: 0.5 } : {}}>
            <Switch
              className="custom-assignment-switch"
              checked={currentRecord[statusKey]} 
              disabled={isDefinited || isCompleted}
              onChange={(checked) => {
                if (isEvaluated) return;

                setControls(prevControls =>
                  prevControls.map(control => {
                    if (control.code === record.code) {
                      const newControl = { ...control, [statusKey]: checked };

                      if (checked) {
                        statusLabels.slice(0, index + 1).forEach(sLabel => {
                          newControl[statusMapping[sLabel.toLowerCase()]] = true;
                        });
                      } else {
                        statusLabels.slice(index).forEach(sLabel => {
                          newControl[statusMapping[sLabel.toLowerCase()]] = false;
                        });
                      }

                      if (statusKey === "designed") {
                        newControl.execution_type_id = null;
                        newControl.executionType = null;
                        newControl.frequency_id = null;
                        newControl.frequency = null;
                      }

                      return newControl;
                    }
                    return control;
                  })
                );

                setEditedControls(prev => new Set(prev).add(record.code));
              }}
            />
          </div>
        );

        return isDefinited ? (
          <Tooltip title="Solo lectura">
            <span>{switchElement}</span>
          </Tooltip>
        ) : (
          switchElement
        );
      }
    };
  }),
  {
    title: "Ejecución y Frecuencia",
    dataIndex: "frequency",
    key: "frequency",
    onHeaderCell: () => ({ 
      style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' } 
    }),
    width: '15%',
    render: (value, record) => {
      const currentRecord = controls.find(control => control.code === record.code) || record;
      const isEvaluated = currentRecord.initialStatus === "Evaluado";
      const disabled = isDisabled(currentRecord) || isEvaluated;
      
      return (
        <div style={isEvaluated ? { opacity: 0.5 } : {}}>
          <div style={{ position: 'relative' }}>
            <Select
              allowClear
              placeholder="Tipo de Ejecución"
              style={{ width: '100%', marginBottom: 8 }}
              value={currentRecord.execution_type_id}
              onChange={(selectedId) => {
                if (isEvaluated) return;
                
                const selectedOption = options.executionTypes.find(opt => opt.id === selectedId);
                setControls(prevControls => 
                  prevControls.map(control => 
                    control.code === record.code 
                      ? { 
                          ...control, 
                          execution_type_id: selectedId,
                          executionType: selectedOption?.name 
                        } 
                      : control
                  )
                );
                setEditedControls(prev => new Set(prev).add(record.code));
              }}
              disabled={disabled}
              status={!disabled && !currentRecord.execution_type_id ? 'error' : ''}
            >
              {options.executionTypes?.map(option => (
                <Option key={option.id} value={option.id}>
                  <Tooltip title={option.name} placement="left">
                    <span>{option.name}</span>
                  </Tooltip>
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ position: 'relative' }}>
            <Select
              allowClear
              placeholder="Frecuencia"
              style={{ width: '100%' }}
              value={currentRecord.frequency_id}
              onChange={(selectedId) => {
                if (isEvaluated) return;
                
                const selectedOption = options.frequencies.find(opt => opt.id === selectedId);
                setControls(prevControls => 
                  prevControls.map(control => 
                    control.code === record.code 
                      ? { 
                          ...control, 
                          frequency_id: selectedId,
                          frequency: selectedOption?.name 
                        } 
                      : control
                  )
                );
                setEditedControls(prev => new Set(prev).add(record.code));
              }}
              disabled={disabled}
              status={!disabled && !currentRecord.frequency_id ? 'error' : ''}
            >
              {options.frequencies?.map(option => (
                <Option key={option.id} value={option.id}>
                  <Tooltip title={option.name} placement="left">
                    <span>{option.name}</span>
                  </Tooltip>
                </Option>
              ))}
            </Select>
          </div>
        </div>
      );
    }
  },
  {
    title: "Log",
    dataIndex: "log",
    key: "log",
    onHeaderCell: () => ({ 
      style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' } 
    }),
    width: '5%',
    render: (_, record) => (
      <div style={{ textAlign: 'center' }}>
        <Tooltip title="Ver Log">
          <FolderOpenFilled 
            style={{ fontSize: 20, cursor: 'pointer', color: '#006939' }}
            onClick={() => handleOpenChangeLog(record)}
          />
        </Tooltip>
      </div>
    )
  }
];

export default Columns;